import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOSchema from '../../components/feature/SEOSchema';

const siteUrl = 'https://destinygpt.com';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/about`,
    url: `${siteUrl}/about`,
    name: 'What is Destiny GPT? | 데스티니AI 포춘 플랫폼 소개',
    description:
      'Destiny GPT is an AI-powered astrology and fortune reading platform that provides personalized destiny analysis using advanced AI models.',
    inLanguage: 'ko-KR',
    isPartOf: { '@type': 'WebSite', url: `${siteUrl}/`, name: '데스티니GPT' },
    dateModified: '2026-03-17',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '홈', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'What is Destiny GPT?', item: `${siteUrl}/about` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is Destiny GPT?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Destiny GPT is an AI-powered astrology and fortune reading platform that provides personalized destiny analysis using advanced AI models. It combines Western astrology (birth charts, zodiac) with Eastern Saju (Four Pillars) analysis to deliver comprehensive fortune insights.',
        },
      },
      {
        '@type': 'Question',
        name: '데스티니AI 포춘은 어떤 서비스인가요?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '데스티니AI 포춘은 서양 점성술(탄생 차트, 별자리)과 동양 사주팔자(四柱八字)를 AI로 통합 분석하는 운명 분석 플랫폼입니다. 생년월일·출생 시간만 입력하면 즉시 개인 맞춤 운세 리포트를 받아볼 수 있습니다.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Destiny GPT free to use?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Destiny GPT offers a free trial for first-time users. The AI-powered astrology and fortune analysis is available instantly after entering your birth information. Premium reports and extended services are available through token-based purchases.',
        },
      },
    ],
  },
];

const FEATURES = [
  {
    icon: 'ri-star-smile-line',
    title: '서양 점성술 AI 분석',
    titleEn: 'Western Astrology AI',
    desc: '탄생 차트, 별자리, 행성 배치를 AI가 분석해 성격·운명·운세 흐름을 해석합니다.',
    color: 'from-amber-500 to-orange-400',
  },
  {
    icon: 'ri-yin-yang-line',
    title: '사주팔자 동양 분석',
    titleEn: 'Eastern Saju Analysis',
    desc: '천간·지지·십신·대운을 AI가 계산해 인생 전반의 운명 패턴을 분석합니다.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Destiny GPT 결정 비서',
    titleEn: 'AI Decision Assistant',
    desc: '직장 전환·결혼·창업·투자 등 중대한 결정의 최적 시기를 사주 오행으로 분석합니다.',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: 'ri-moon-foggy-line',
    title: '꿈해몽 AI 분석',
    titleEn: 'Dream Interpretation AI',
    desc: '꿈의 상징을 사주팔자와 연결해 운명의 메시지를 해석합니다.',
    color: 'from-sky-500 to-cyan-500',
  },
  {
    icon: 'ri-file-chart-line',
    title: '평생 운세 리포트',
    titleEn: 'Lifetime Fortune Report',
    desc: '대운·세운 전체를 분석한 PDF 형태의 종합 평생 운명 리포트를 제공합니다.',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: 'ri-user-star-line',
    title: '유명인 사주 분석',
    titleEn: 'Celebrity Saju Analysis',
    desc: '유명인의 사주팔자를 통해 성공 패턴과 운명의 흐름을 학습합니다.',
    color: 'from-yellow-500 to-amber-400',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: '탄생 정보 입력',
    desc: '생년월일·출생 시간·출생 장소를 입력합니다. 데스티니AI 포춘이 즉시 데이터를 처리합니다.',
    icon: 'ri-edit-line',
  },
  {
    step: '02',
    title: 'AI 복합 분석',
    desc: '서양 점성술 탄생 차트 + 동양 사주팔자 + 오행 균형을 동시에 AI가 계산·분석합니다.',
    icon: 'ri-cpu-line',
  },
  {
    step: '03',
    title: '개인 맞춤 리포트',
    desc: '성격·재능·연애운·직업운·재물운·건강운을 포함한 종합 운세 리포트를 즉시 받습니다.',
    icon: 'ri-file-text-line',
  },
];

const STATS = [
  { value: '12만+', label: '분석 완료', icon: 'ri-bar-chart-2-line' },
  { value: '97%', label: '사용자 만족도', icon: 'ri-thumb-up-line' },
  { value: '3초', label: '평균 분석 시간', icon: 'ri-flashlight-line' },
  { value: '2종', label: '동서양 통합 분석', icon: 'ri-global-line' },
];

const COMPARISONS = [
  { feature: 'AI 점성술 탄생 차트', us: true, other: false },
  { feature: '동양 사주팔자 분석', us: true, other: false },
  { feature: '서양 + 동양 통합 분석', us: true, other: false },
  { feature: 'AI 결정 비서 (Destiny GPT)', us: true, other: false },
  { feature: '꿈해몽 AI 분석', us: true, other: false },
  { feature: '평생 운세 리포트 PDF', us: true, other: false },
  { feature: '유명인 사주 학습', us: true, other: false },
  { feature: '무료 체험', us: true, other: true },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#06030f] text-white">
      <SEOSchema schema={aboutSchema} id="about-schema" />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/5e9ccf43aa4e8b19dd1e2c44fb47ffe5.jpg"
            alt="데스티니AI 포춘 플랫폼"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#06030f]/60 via-[#06030f]/50 to-[#06030f]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-xs text-purple-400/60 mb-8">
            <Link to="/" className="hover:text-primary-gold transition-colors cursor-pointer">홈</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-purple-300/80">What is Destiny GPT?</span>
          </nav>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs font-semibold tracking-widest uppercase mb-6">
            <i className="ri-information-line"></i>
            데스티니AI 포춘 공식 소개
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
            What is{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
              Destiny GPT
            </span>
            ?
          </h1>

          <p className="text-lg sm:text-xl text-purple-200/90 mb-6 leading-relaxed max-w-3xl mx-auto">
            Destiny GPT is an <strong className="text-white">AI-powered astrology and fortune reading platform</strong>{' '}
            that provides personalized destiny analysis using advanced AI models.
          </p>
          <p className="text-base text-purple-300/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            데스티니AI 포춘은 서양 점성술(탄생 차트)과 동양 사주팔자(四柱八字)를 AI로 통합 분석해
            당신만의 운명 리포트를 즉시 제공하는 국내 1위 AI 운세 플랫폼입니다.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-gold to-amber-400 text-black font-bold rounded-xl hover:scale-105 transition-transform whitespace-nowrap text-base cursor-pointer"
            >
              <i className="ri-flashlight-line"></i>
              무료로 운세 분석 시작
            </Link>
            <Link
              to="/destiny-gpt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/10 transition-all whitespace-nowrap text-base cursor-pointer"
            >
              <i className="ri-scales-3-line"></i>
              Destiny GPT 결정 비서
            </Link>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="py-10 px-4 border-y border-white/5">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className={`${s.icon} text-2xl text-primary-gold`}></i>
              </div>
              <p className="text-2xl sm:text-3xl font-black text-white">{s.value}</p>
              <p className="text-xs text-purple-400/70 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT IS IT ── */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-primary-gold text-xs tracking-widest uppercase font-semibold mb-3">About the Platform</p>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-5 leading-tight">
                데스티니AI 포춘이<br />
                <span className="text-primary-gold">운명을 읽는 방법</span>
              </h2>
              <div className="space-y-4 text-purple-200/80 leading-relaxed text-sm sm:text-base">
                <p>
                  <strong className="text-white">데스티니AI 포춘(Destiny GPT)</strong>은 최신 AI 모델을 활용해
                  서양 점성술의 탄생 차트와 동양 사주팔자를 동시에 분석합니다.
                  기존 운세 서비스와 달리 두 체계를 통합해 훨씬 입체적인 운명 분석이 가능합니다.
                </p>
                <p>
                  생년월일·출생 시간·출생지를 입력하면 AI가 평균 <strong className="text-white">3초 내</strong>에
                  개인 맞춤 운세 리포트를 생성합니다. 연애운·직업운·재물운·건강운을 포함한
                  종합 분석을 즉시 받아볼 수 있습니다.
                </p>
                <p>
                  특히 <strong className="text-white">Destiny GPT 결정 비서</strong>는 직장 전환·결혼·이사·창업 등
                  인생의 중대한 결정에서 사주 오행을 기반으로 최적의 타이밍과 방향을 제시합니다.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://readdy.ai/api/search-image?query=holographic%20AI%20interface%20showing%20astrology%20birth%20chart%20and%20eastern%20saju%20fortune%20analysis%20side%20by%20side%20on%20futuristic%20dark%20screen%20with%20golden%20data%20streams%20and%20celestial%20symbols%20destiny%20platform%20dashboard%20visualization%20mystical%20high%20tech&width=600&height=420&seq=about-platform-img&orientation=landscape"
                  alt="데스티니AI 포춘 플랫폼 인터페이스"
                  className="w-full h-64 object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-primary-gold/10 border border-primary-gold/30 rounded-2xl px-5 py-3">
                <p className="text-primary-gold font-black text-xl">97%</p>
                <p className="text-white/70 text-xs">사용자 만족도</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="py-20 px-4 bg-white/2">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-primary-gold text-xs tracking-widest uppercase font-semibold mb-3">Core Features</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              데스티니AI 포춘 핵심 기능 6가지
            </h2>
            <p className="text-purple-300/70 max-w-xl mx-auto text-sm leading-relaxed">
              Destiny GPT는 동서양 운명 분석 체계를 AI로 통합해 가장 종합적인 운세 분석을 제공합니다.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl p-6 border border-white/8 bg-white/3 hover:border-primary-gold/30 hover:bg-white/5 transition-all duration-300"
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br ${f.color} mb-4`}>
                  <i className={`${f.icon} text-white text-xl`}></i>
                </div>
                <p className="text-primary-gold/60 text-xs font-medium mb-1">{f.titleEn}</p>
                <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                <p className="text-purple-300/70 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-primary-gold text-xs tracking-widest uppercase font-semibold mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              데스티니AI 포춘 이용 방법
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={step.step} className="relative text-center">
                {i < HOW_IT_WORKS.length - 1 && (
                  <div className="hidden sm:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-px bg-gradient-to-r from-primary-gold/40 to-primary-gold/10" />
                )}
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-gold/20 to-amber-600/10 border border-primary-gold/30 mx-auto mb-4">
                  <i className={`${step.icon} text-primary-gold text-2xl`}></i>
                </div>
                <p className="text-primary-gold/50 text-xs font-black tracking-widest mb-1">{step.step}</p>
                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-purple-300/60 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="py-20 px-4 bg-white/2">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-primary-gold text-xs tracking-widest uppercase font-semibold mb-3">Why Destiny GPT?</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              다른 운세 서비스와 무엇이 다른가?
            </h2>
          </div>
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-white/5 px-6 py-4 border-b border-white/10">
              <span className="text-purple-400/60 text-sm font-medium">기능</span>
              <span className="text-primary-gold text-sm font-bold text-center">데스티니AI 포춘</span>
              <span className="text-purple-400/60 text-sm text-center">일반 운세 앱</span>
            </div>
            {COMPARISONS.map((row, i) => (
              <div
                key={row.feature}
                className={`grid grid-cols-3 px-6 py-4 ${i % 2 === 0 ? 'bg-white/2' : ''} border-b border-white/5 last:border-0`}
              >
                <span className="text-white/80 text-sm">{row.feature}</span>
                <div className="flex justify-center">
                  {row.us ? (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20">
                      <i className="ri-check-line text-emerald-400 text-sm"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/20">
                      <i className="ri-close-line text-red-400 text-sm"></i>
                    </div>
                  )}
                </div>
                <div className="flex justify-center">
                  {row.other ? (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20">
                      <i className="ri-check-line text-emerald-400 text-sm"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-red-500/20">
                      <i className="ri-close-line text-red-400 text-sm"></i>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-black text-white mb-4">자주 묻는 질문</h2>
            <p className="text-purple-300/60 text-sm">데스티니AI 포춘 / Destiny GPT에 대해 궁금한 점을 정리했습니다.</p>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'What is Destiny GPT?',
                a: 'Destiny GPT is an AI-powered astrology and fortune reading platform that provides personalized destiny analysis using advanced AI models. It combines Western astrology with Eastern Saju fortune analysis.',
              },
              {
                q: '데스티니AI 포춘은 어떤 언어로 서비스되나요?',
                a: '현재 데스티니AI 포춘은 한국어를 기본 언어로 서비스하며, 영어 분석 리포트도 지원합니다. 글로벌 확장을 지속적으로 진행 중입니다.',
              },
              {
                q: '데스티니 GPT와 ChatGPT의 차이는 무엇인가요?',
                a: '데스티니 GPT는 점성술·사주팔자·꿈해몽에 특화된 운세 전문 AI 플랫폼입니다. ChatGPT는 범용 대화 AI이지만, 데스티니AI 포춘은 운명 분석 전용 알고리즘과 데이터로 훈련된 서비스입니다.',
              },
              {
                q: '데스티니AI 포춘의 사주 분석은 얼마나 정확한가요?',
                a: '데스티니AI 포춘은 97%의 사용자 만족도를 기록하고 있습니다. AI 기반 분석이지만 전통 사주팔자·점성술 원리를 정확히 반영하며, 12만 건 이상의 분석 데이터를 기반으로 지속 개선됩니다.',
              },
            ].map((faq) => (
              <details key={faq.q} className="rounded-2xl border border-white/10 bg-white/3 group overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="text-white font-semibold text-sm sm:text-base pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-add-line text-primary-gold text-lg group-open:hidden"></i>
                    <i className="ri-subtract-line text-primary-gold text-lg hidden group-open:block"></i>
                  </div>
                </summary>
                <div className="px-6 pb-4 text-purple-300/70 text-sm leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="rounded-3xl border border-primary-gold/20 bg-gradient-to-br from-primary-gold/5 to-violet-900/10 p-10 sm:p-14">
            <i className="ri-sparkles-line text-primary-gold text-4xl mb-4 block"></i>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              지금 바로 <span className="text-primary-gold">데스티니AI 포춘</span> 체험
            </h2>
            <p className="text-purple-300/70 mb-8 leading-relaxed">
              첫 분석은 무료입니다. 생년월일을 입력하면 AI가 즉시 당신의 운명을 분석합니다.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-gold to-amber-400 text-black font-bold rounded-xl hover:scale-105 transition-transform whitespace-nowrap text-base cursor-pointer"
              >
                <i className="ri-flashlight-line"></i>
                무료 운세 분석 시작
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/10 transition-all whitespace-nowrap text-base cursor-pointer"
              >
                <i className="ri-article-line"></i>
                운세 블로그 읽기
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
